import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { 
  Layers, 
  Plus, 
  Trash2, 
  ChevronRight, 
  ChevronDown,
  Image as ImageIcon,
  Check,
  X
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { API_URL } from '../../config';

const CategoryListScreen = () => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState({});
  const [showAddModal, setShowAddModal] = useState(false);
  const [newCategory, setNewCategory] = useState({ name: '', image: '', parent: null });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { getConfig } = useAuth();

  const fetchCategories = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/categories?showAll=true`);
      setCategories(data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching categories:', error);
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  const toggleExpand = (id) => {
    setExpanded(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const handleAddCategory = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      await axios.post(`${API_URL}/categories`, newCategory, getConfig());
      setShowAddModal(false);
      setNewCategory({ name: '', image: '', parent: null });
      fetchCategories();
    } catch (error) {
      alert(error.response?.data?.message || 'Error creating category');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure? This will also delete all subcategories.')) {
      try {
        await axios.delete(`${API_URL}/categories/${id}`, getConfig());
        fetchCategories();
      } catch (error) {
        alert('Error deleting category');
      }
    }
  };

  return (
    <div className="p-8 space-y-8 max-w-6xl mx-auto">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-black italic text-secondary-900 uppercase">Categories</h1>
          <p className="text-secondary-500">Manage product categories and hierarchies.</p>
        </div>
        <button 
          onClick={() => {
            setNewCategory({ name: '', image: '', parent: null });
            setShowAddModal(true);
          }}
          className="bg-primary-600 hover:bg-primary-700 text-white px-6 py-3 rounded-2xl font-bold shadow-lg shadow-primary-200 flex items-center space-x-2 transition-all"
        >
          <Plus className="h-5 w-5" />
          <span>Add Category</span>
        </button>
      </div>

      <div className="grid gap-4">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-600"></div>
          </div>
        ) : (
          categories.map(cat => (
            <div key={cat._id} className="bg-white rounded-3xl border border-secondary-100 overflow-hidden shadow-sm hover:shadow-md transition-shadow">
              <div className="p-5 flex items-center justify-between group">
                <div className="flex items-center space-x-4">
                  <div className="h-12 w-12 rounded-xl bg-secondary-50 flex items-center justify-center overflow-hidden border border-secondary-100">
                    {cat.image ? (
                      <img src={cat.image} alt={cat.name} className="w-full h-full object-cover" />
                    ) : (
                      <Layers className="h-6 w-6 text-secondary-400" />
                    )}
                  </div>
                  <div>
                    <h3 className="font-bold text-secondary-900 text-lg">{cat.name}</h3>
                    <p className="text-xs text-secondary-400 font-medium uppercase tracking-wider">{cat.subcategories?.length || 0} Subcategories</p>
                  </div>
                </div>

                <div className="flex items-center space-x-2">
                  <button 
                    onClick={() => {
                      setNewCategory({ name: '', image: '', parent: cat._id });
                      setShowAddModal(true);
                    }}
                    className="p-2 text-primary-600 hover:bg-primary-50 rounded-xl transition-all"
                    title="Add Subcategory"
                  >
                    <Plus className="h-5 w-5" />
                  </button>
                  <button 
                    onClick={() => handleDelete(cat._id)}
                    className="p-2 text-secondary-400 hover:text-red-500 hover:bg-red-50 rounded-xl transition-all"
                  >
                    <Trash2 className="h-5 w-5" />
                  </button>
                  {cat.subcategories?.length > 0 && (
                    <button 
                      onClick={() => toggleExpand(cat._id)}
                      className="p-2 text-secondary-400 hover:text-secondary-900 bg-secondary-50 rounded-xl transition-all"
                    >
                      {expanded[cat._id] ? <ChevronDown className="h-5 w-5" /> : <ChevronRight className="h-5 w-5" />}
                    </button>
                  )}
                </div>
              </div>

              {expanded[cat._id] && cat.subcategories?.length > 0 && (
                <div className="bg-secondary-50/50 border-t border-secondary-50 p-4 space-y-2">
                  {cat.subcategories.map(sub => (
                    <div key={sub._id} className="bg-white ml-8 p-4 rounded-2xl border border-secondary-100 flex items-center justify-between group/sub">
                      <div className="flex items-center space-x-3">
                        <div className="h-8 w-8 rounded-lg bg-secondary-50 flex items-center justify-center overflow-hidden">
                          {sub.image ? (
                            <img src={sub.image} alt={sub.name} className="w-full h-full object-cover" />
                          ) : (
                            <Layers className="h-4 w-4 text-secondary-400" />
                          )}
                        </div>
                        <span className="font-bold text-secondary-700">{sub.name}</span>
                      </div>
                      <button 
                        onClick={() => handleDelete(sub._id)}
                        className="p-2 text-secondary-400 hover:text-red-500 hover:bg-red-50 rounded-xl transition-all opacity-0 group-hover/sub:opacity-100"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))
        )}
      </div>

      {/* Add Modal */}
      {showAddModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-secondary-900/60 backdrop-blur-sm">
          <div className="bg-white rounded-[32px] w-full max-w-md overflow-hidden shadow-2xl">
            <div className="p-8 space-y-6">
              <div className="flex justify-between items-center">
                <h2 className="text-2xl font-black italic text-secondary-900 uppercase">
                  {newCategory.parent ? 'Add Subcategory' : 'Add Category'}
                </h2>
                <button onClick={() => setShowAddModal(false)} className="p-2 hover:bg-secondary-100 rounded-full transition-all">
                  <X className="h-6 w-6 text-secondary-400" />
                </button>
              </div>

              <form onSubmit={handleAddCategory} className="space-y-4">
                <div className="space-y-2">
                  <label className="text-xs font-black text-secondary-400 uppercase tracking-widest ml-1">Name</label>
                  <input 
                    required
                    type="text" 
                    value={newCategory.name}
                    onChange={(e) => setNewCategory({...newCategory, name: e.target.value})}
                    placeholder="e.g. Electronics"
                    className="w-full bg-secondary-50 border-none rounded-2xl py-4 px-6 focus:ring-2 focus:ring-primary-500 outline-none font-bold text-secondary-900"
                  />
                </div>

                <div className="space-y-2">
                  <label className="text-xs font-black text-secondary-400 uppercase tracking-widest ml-1">Category Image</label>
                  <div className="flex flex-col space-y-4">
                    <div className="relative">
                      <ImageIcon className="absolute left-4 top-4 h-5 w-5 text-secondary-400" />
                      <input 
                        type="text" 
                        value={newCategory.image}
                        onChange={(e) => setNewCategory({...newCategory, image: e.target.value})}
                        placeholder="Paste image URL here (Optional)"
                        className="w-full bg-secondary-50 border-none rounded-2xl py-4 pl-12 pr-6 focus:ring-2 focus:ring-primary-500 outline-none font-bold text-secondary-900"
                      />
                    </div>

                    <div className="flex items-center space-x-4">
                      <div className="h-px bg-secondary-100 flex-1" />
                      <span className="text-[10px] font-black text-secondary-300 uppercase tracking-widest">OR</span>
                      <div className="h-px bg-secondary-100 flex-1" />
                    </div>

                    <div className="relative">
                      <input 
                        type="file" 
                        id="categoryImageUpload"
                        className="hidden" 
                        onChange={async (e) => {
                          const file = e.target.files[0];
                          const uploadFormData = new FormData();
                          uploadFormData.append('image', file);
                          setIsSubmitting(true);
                          try {
                            const { data } = await axios.post(`${API_URL}/upload`, uploadFormData, {
                              headers: { 'Content-Type': 'multipart/form-data' }
                            });
                            setNewCategory({ ...newCategory, image: data.image });
                          } catch (error) {
                            alert('Upload failed');
                          } finally {
                            setIsSubmitting(false);
                          }
                        }}
                      />
                      <label 
                        htmlFor="categoryImageUpload"
                        className="w-full flex items-center justify-center space-x-2 bg-secondary-900 text-white py-4 rounded-2xl font-black italic uppercase cursor-pointer hover:bg-secondary-800 transition-all shadow-xl shadow-secondary-200"
                      >
                        <Plus className="h-5 w-5" />
                        <span>Upload from Computer</span>
                      </label>
                    </div>
                  </div>
                </div>


                <div className="pt-4 flex space-x-3">
                  <button 
                    type="button"
                    onClick={() => setShowAddModal(false)}
                    className="flex-1 px-6 py-4 rounded-2xl font-bold text-secondary-500 hover:bg-secondary-50 transition-all"
                  >
                    Cancel
                  </button>
                  <button 
                    disabled={isSubmitting}
                    className="flex-[2] bg-primary-600 hover:bg-primary-700 disabled:bg-primary-300 text-white px-6 py-4 rounded-2xl font-bold shadow-lg shadow-primary-200 transition-all flex items-center justify-center space-x-2"
                  >
                    {isSubmitting ? (
                       <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-b-2 border-white"></div>
                    ) : (
                      <>
                        <Check className="h-5 w-5" />
                        <span>Save Category</span>
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CategoryListScreen;
